import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getTenants, impersonateTenant, TenantStats } from '../../../api/backoffice';
import { io } from "socket.io-client";
import { ArrowRight, Search, Activity, Users, Clock } from 'lucide-react';

export const CustomersPage: React.FC = () => {
    const { t } = useTranslation('common');
    const [tenants, setTenants] = useState<TenantStats[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        loadTenants();

        // Use Socket.io for robust real-time updates with automatic session handling
        const socket = io(window.location.origin, {
            withCredentials: true,
            transports: ['polling', 'websocket']
        });

        socket.on('connect', () => {
            console.log('Socket.io connected with ID:', socket.id);
            (window as any).socketStatus = 'connected';
            socket.emit('backoffice:join');
        });

        socket.on('connect_error', (err) => {
            console.error('Socket.io connection error:', err);
            (window as any).socketStatus = 'error: ' + err.message;
        });

        socket.on('disconnect', (reason) => {
            console.log('Socket.io disconnected:', reason);
            (window as any).socketStatus = 'disconnected: ' + reason;
        });

        // Merge, don't replace: this push is debounced and DB-sourced, independent of whatever
        // is currently rendered (e.g. right after a fresh REST load whose data this event's
        // snapshot may not yet agree with). A blind replace can silently drop a row the admin
        // is mid-interaction with the instant this fires — merge updates live stats for known
        // tenants and adds genuinely new ones, but never removes a row this way.
        socket.on('backoffice:stats-update', (data: TenantStats[]) => {
            setTenants(prev => {
                const merged = new Map(prev.map(t => [t.id, t]));
                for (const incoming of data) merged.set(incoming.id, { ...merged.get(incoming.id), ...incoming });
                return Array.from(merged.values());
            });
        });

        return () => {
            socket.disconnect();
        };
    }, []);

    const loadTenants = async () => {
        try {
            setLoading(true);
            const data = await getTenants();
            setTenants(data);
        } catch (err: any) {
            setError(err.message || 'Failed to load tenants');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleImpersonate = async (e: React.MouseEvent, accountId: string) => {
        e.stopPropagation(); // Prevent row click
        try {
            await impersonateTenant(accountId);
            window.location.href = '/';
        } catch (err) {
            console.error('Impersonation failed:', err);
            alert('Failed to impersonate tenant');
        }
    };

    const filteredTenants = tenants.filter(t =>
        t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (t.website && t.website.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    if (loading) return (
        <div className="flex items-center justify-center h-full text-text-muted">
            <Activity className="animate-spin mr-2" /> {t('backoffice.customers.loading')}
        </div>
    );

    if (error) return (
        <div className="flex items-center justify-center h-full text-red-400">
            Error: {error}
        </div>
    );

    return (
        <div className="space-y-6">
            <header className="flex justify-between items-center mb-8">
                <div>
                    <h1 className="text-2xl font-bold text-white">{t('backoffice.customers.title')}</h1>
                    <p className="text-text-muted text-sm mt-1">{t('backoffice.customers.subtitle')}</p>
                </div>
                <div className="relative w-64">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Search className="h-4 w-4 text-text-muted" />
                    </div>
                    <input
                        type="text"
                        placeholder={t('backoffice.customers.search')}
                        className="bg-[#1e293b] text-gray-200 text-sm rounded-lg block w-full pl-10 p-2.5 border border-[#334155] focus:ring-blue-500 focus:border-blue-500 placeholder-gray-500"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </header>

            <div className="bg-[#1e293b] rounded-lg shadow-sm border border-[#334155] overflow-hidden">
                <table className="min-w-full divide-y divide-[#334155]">
                    <thead className="bg-slate-nav">
                        <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
                                {t('backoffice.customers.customer')}
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
                                {t('backoffice.customers.contact')}
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
                                {t('backoffice.customers.status')}
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
                                {t('backoffice.customers.stats')}
                            </th>
                            <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-text-muted uppercase tracking-wider">
                                {t('backoffice.customers.actions')}
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-[#1e293b] divide-y divide-[#334155]">
                        {filteredTenants.map((tenant) => (
                            <tr
                                key={tenant.id}
                                onClick={() => navigate(`/backoffice/customers/${tenant.id}`)}
                                className="hover:bg-[#2a3848] transition-colors cursor-pointer group"
                            >
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center">
                                        <div className="h-10 w-10 flex-shrink-0 bg-blue-900/50 rounded-lg flex items-center justify-center text-blue-400 font-bold border border-blue-800">
                                            {tenant.name.charAt(0).toUpperCase()}
                                        </div>
                                        <div className="ml-4">
                                            <div className="text-sm font-medium text-white group-hover:text-blue-400 transition-colors">
                                                {tenant.name}
                                            </div>
                                            <div className="text-sm text-text-muted">{tenant.website || t('backoffice.customers.noWebsite')}</div>
                                        </div>
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-300">{tenant.billingEmail || '-'}</div>
                                    <div className="text-xs text-text-muted">ID: {tenant.id.substring(0, 8)}...</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-900/30 text-green-400 border border-green-800/50">
                                        {t('backoffice.customers.active')}
                                    </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex space-x-4 text-xs text-text-muted">
                                        <div className="flex items-center" title={t('backoffice.customers.agents')}>
                                            <Users size={14} className="mr-1" /> {tenant.agentCount}
                                        </div>
                                        <div className="flex items-center" title={t('backoffice.customers.visitors')}>
                                            <Activity size={14} className="mr-1" />
                                            <span data-testid="visitor-count">{tenant.visitorCount}</span>
                                        </div>
                                        <div className="flex items-center" title={t('backoffice.customers.createdAt')}>
                                            <Clock size={14} className="mr-1" /> {new Date(tenant.createdAt).toLocaleDateString()}
                                        </div>
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    <button
                                        onClick={(e) => handleImpersonate(e, tenant.id)}
                                        className="text-blue-400 hover:text-blue-300 mr-4 font-medium transition-colors"
                                    >
                                        {t('backoffice.customers.impersonate')}
                                    </button>
                                    <span className="text-text-muted group-hover:text-gray-300 transition-colors">
                                        <ArrowRight size={16} className="inline" />
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {filteredTenants.length === 0 && (
                    <div className="p-8 text-center text-text-muted">
                        {t('backoffice.customers.noResults', { searchTerm })}
                    </div>
                )}
            </div>
        </div>
    );
};
